import React from 'react'
import { authBaseUrl, authAppId } from './authApiConfig'

// Central registration creates the account and mails a verification link; no
// session exists until the user clicks it. 'authenticated' means a token was
// issued and the session is live (the pre-federation local behaviour).
export type RegisterOutcome = 'authenticated' | 'verification-pending'

export interface StructuraAuthState {
  isSupported: boolean
  isAuthenticated: boolean
  isActive: boolean
  isAdmin: boolean
  isLoading: boolean
  userName?: string
  userEmail?: string
  userPicture?: string
  errorMessage?: string
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name?: string, companyName?: string) => Promise<RegisterOutcome>
  verifyEmail: (verificationToken: string) => Promise<void>
  logout: () => void
  getAccessToken: () => Promise<string>
}

const StructuraAuthContext = React.createContext<StructuraAuthState | undefined>(undefined)

const decodeBase64Url = (segment: string): string => {
  let base64: string = segment.replace(/-/g, '+').replace(/_/g, '/')
  const paddingNeeded: number = (4 - (base64.length % 4)) % 4
  base64 += '='.repeat(paddingNeeded)
  return atob(base64)
}

const parseJwtPayload = (jwt: string): Record<string, unknown> | undefined => {
  if (jwt.length === 0) {
    return undefined
  }
  try {
    const parts: string[] = jwt.split('.')
    if (parts.length !== 3) {
      return undefined
    }
    const payload: string = decodeBase64Url(parts[1])
    return JSON.parse(payload)
  } catch {
    return undefined
  }
}

const getRoleFromToken = (jwt: string): string | undefined => {
  const claims = parseJwtPayload(jwt)
  if (claims === undefined) {
    return undefined
  }
  return typeof claims.role === 'string' ? claims.role : undefined
}

const isTokenExpiredStatic = (jwt: string): boolean => {
  const claims = parseJwtPayload(jwt)
  if (claims === undefined) {
    return true
  }
  if (typeof claims.exp !== 'number') {
    return false
  }
  const expirationTime: number = claims.exp * 1000
  const now: number = Date.now()
  return now >= expirationTime
}

export const StructuraAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [token, setToken] = React.useState<string>(() => {
    const stored: string | null = window.localStorage.getItem('structura.auth.token')
    if (stored !== null && isTokenExpiredStatic(stored)) {
      window.localStorage.removeItem('structura.auth.token')
      window.localStorage.removeItem('structura.auth.email')
      window.localStorage.removeItem('structura.auth.name')
      return ''
    }
    return stored ?? ''
  })
  const [userName, setUserName] = React.useState<string | undefined>(() => {
    const storedToken: string | null = window.localStorage.getItem('structura.auth.token')
    if (storedToken !== null && isTokenExpiredStatic(storedToken)) {
      return undefined
    }
    const stored: string | null = window.localStorage.getItem('structura.auth.name')
    return stored ?? undefined
  })
  const [userEmail, setUserEmail] = React.useState<string | undefined>(() => {
    const storedToken: string | null = window.localStorage.getItem('structura.auth.token')
    if (storedToken !== null && isTokenExpiredStatic(storedToken)) {
      return undefined
    }
    const stored: string | null = window.localStorage.getItem('structura.auth.email')
    return stored ?? undefined
  })
  const [isActive, setIsActive] = React.useState<boolean>(() => {
    const storedToken: string | null = window.localStorage.getItem('structura.auth.token')
    if (storedToken !== null && isTokenExpiredStatic(storedToken)) {
      return false
    }
    const stored: string | null = window.localStorage.getItem('structura.auth.isActive')
    return stored === 'true'
  })
  const isAdmin: boolean = React.useMemo(() => {
    if (token.length === 0) {
      return false
    }
    return getRoleFromToken(token) === 'Admin'
  }, [token])
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined)

  const isAuthenticated: boolean = token.length > 0

  const persistSession = React.useCallback((nextToken: string, email: string, name?: string, active?: boolean): void => {
    window.localStorage.setItem('structura.auth.token', nextToken)
    window.localStorage.setItem('structura.auth.email', email)
    if (name !== undefined) {
      window.localStorage.setItem('structura.auth.name', name)
    }
    window.localStorage.setItem('structura.auth.isActive', active === true ? 'true' : 'false')
  }, [])

  const clearSession = React.useCallback((): void => {
    window.localStorage.removeItem('structura.auth.token')
    window.localStorage.removeItem('structura.auth.email')
    window.localStorage.removeItem('structura.auth.name')
    window.localStorage.removeItem('structura.auth.isActive')
  }, [])

  const login = React.useCallback(async (email: string, password: string): Promise<void> => {
    setErrorMessage(undefined)
    setIsLoading(true)
    try {
      const response = await fetch(`${authBaseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => undefined)
        const message: string =
          (payload !== undefined && typeof payload.error === 'string')
            ? payload.error
            : `Login failed (${response.status})`
        setErrorMessage(message)
        throw new Error(message)
      }

      const payload = await response.json()
      const nextToken: string = payload.token ?? ''
      const nextEmail: string = payload.email ?? email
      const nextName: string | undefined = payload.name ?? undefined
      const nextIsActive: boolean = payload.isActive === true

      if (nextToken.length === 0) {
        const message = 'Login response did not include a token.'
        setErrorMessage(message)
        throw new Error(message)
      }

      setToken(nextToken)
      setUserEmail(nextEmail)
      setUserName(nextName)
      setIsActive(nextIsActive)
      persistSession(nextToken, nextEmail, nextName, nextIsActive)
    } finally {
      setIsLoading(false)
    }
  }, [persistSession])

  const register = React.useCallback(async (email: string, password: string, name?: string, companyName?: string): Promise<RegisterOutcome> => {
    setErrorMessage(undefined)
    setIsLoading(true)
    try {
      const response = await fetch(`${authBaseUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name, companyName, appId: authAppId })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => undefined)
        const message: string =
          (payload !== undefined && typeof payload.error === 'string')
            ? payload.error
            : `Registration failed (${response.status})`
        setErrorMessage(message)
        throw new Error(message)
      }

      const payload = await response.json()
      const nextToken: string = payload.token ?? ''
      const nextEmail: string = payload.email ?? email
      const nextName: string | undefined = payload.name ?? name
      const nextIsActive: boolean = payload.isActive === true

      if (nextToken.length === 0) {
        // Central register issues no token for an unverified account — the
        // session starts when the mailed verification link is consumed.
        if (payload.emailVerified === false) {
          return 'verification-pending'
        }
        const message = 'Registration response did not include a token.'
        setErrorMessage(message)
        throw new Error(message)
      }

      setToken(nextToken)
      setUserEmail(nextEmail)
      setUserName(nextName)
      setIsActive(nextIsActive)
      persistSession(nextToken, nextEmail, nextName, nextIsActive)
      return 'authenticated'
    } finally {
      setIsLoading(false)
    }
  }, [persistSession])

  const verifyEmail = React.useCallback(async (verificationToken: string): Promise<void> => {
    setErrorMessage(undefined)
    setIsLoading(true)
    try {
      const response = await fetch(`${authBaseUrl}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: verificationToken })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => undefined)
        const message: string =
          (payload !== undefined && typeof payload.error === 'string')
            ? payload.error
            : `E-mail verification failed (${response.status})`
        setErrorMessage(message)
        throw new Error(message)
      }

      // Frictionless: the central service verifies AND logs the user in.
      const payload = await response.json()
      const nextToken: string = payload.token ?? ''
      const nextEmail: string = payload.email ?? ''
      const nextName: string | undefined = payload.name ?? undefined
      const nextIsActive: boolean = payload.isActive === true

      if (nextToken.length === 0) {
        const message = 'Verification response did not include a token.'
        setErrorMessage(message)
        throw new Error(message)
      }

      setToken(nextToken)
      setUserEmail(nextEmail)
      setUserName(nextName)
      setIsActive(nextIsActive)
      persistSession(nextToken, nextEmail, nextName, nextIsActive)
    } finally {
      setIsLoading(false)
    }
  }, [persistSession])

  const logout = React.useCallback((): void => {
    setToken('')
    setUserEmail(undefined)
    setUserName(undefined)
    setIsActive(false)
    clearSession()
  }, [clearSession])

  const getAccessToken = React.useCallback(async (): Promise<string> => {
    if (isTokenExpiredStatic(token)) {
      setToken('')
      setUserEmail(undefined)
      setUserName(undefined)
      setIsActive(false)
      clearSession()
      throw new Error('Session expired. Please log in again.')
    }
    return token
  }, [token, clearSession])

  const state: StructuraAuthState = {
    isSupported: true,
    isAuthenticated,
    isActive,
    isAdmin,
    isLoading,
    userName,
    userEmail,
    userPicture: undefined,
    errorMessage,
    login,
    register,
    verifyEmail,
    logout,
    getAccessToken
  }

  return (
    <StructuraAuthContext.Provider value={state}>
      {children}
    </StructuraAuthContext.Provider>
  )
}

export const useStructuraAuth = (): StructuraAuthState => {
  const context = React.useContext(StructuraAuthContext)
  if (context === undefined) {
    throw new Error('useStructuraAuth must be used within a StructuraAuthProvider')
  }
  return context
}
